import React, { createContext, useEffect, useMemo, useState } from "react";
import { CustomView, ICoordinates, ILocation } from "types/CustomView";
import { FilterSet, Option } from 'types/Option';
import { DogSummary, FILTER_KEY, FILTER_TYPE } from "types/Dog";
import { Institution } from "types/Institution";
import { APIError, DataProvider, EndPoint } from "data/Provider";
import { MockGeoLocation } from "types/MockGeoLocation";
import { filterList } from "helpers/filterDogList";
import { sortDogs } from "helpers/sortDogs";
import equal from 'fast-deep-equal/es6';


interface UserSave {
    filters: FilterSet,
    customView?: CustomView;
}

interface IHeaderContext {
    loading: boolean;
    dogListError: APIError;
    filteredDogsList: DogSummary[];

    sortType: string;
    setSortType(type: string): void;

    selectedDogIndex: number;
    setSelectedDogIndex: React.Dispatch<React.SetStateAction<number>>;

    customView: CustomView;
    commitViewForm(customView: CustomView): Promise<void | Option[]>;
    userNearestCity;

    filterOptions: FilterSet;
    selectedFilters: FilterSet;
    filterError: APIError;
    validFilters: FilterSet;
    selectFilters(filters: Option[], key: string): void;
    removeFilter(filter: Option, type: FILTER_TYPE): void;
    removeAllFilters(): void;

    filteredInstitutionList: Institution[];
    institutionsError: APIError;
    updateInstitutionSearchText(searchValue: any): void;
    institutionSearchText: Option;
    dogsWithinProximityList: DogSummary[];
}

export const DataContext = createContext<IHeaderContext>({} as IHeaderContext);

export const DogProvider = (props) => {
    const [allDogsList, setAllDogsList] = useState<DogSummary[]>(null);
    const [dogsWithinProximityList, setDogsWithinProximityList] = useState<DogSummary[]>(null);
    const [dogListError, setDogListError] = useState<APIError>();
    const [loading, setLoading] = useState(true);

    const [filterOptions, setFilterOptions] = useState<FilterSet>(null);
    const [filterError, setFilterError] = useState<APIError>();

    const [dogListSortType, setDogListSortType] = useState('name');
    const [selectedFilters, setSelectedFilters] = useState<FilterSet>({});
    const [selectedDogIndex, setSelectedDogIndex] = useState<number>(null);
    const [customView, setCustomView] = useState<CustomView>(null);
    const [lat, setLat] = useState<number>(null);
    const [long, setLong] = useState<number>(null);

    const [institutionList, setInstitionList] = useState<Institution[]>([]);
    const [institutionsError, setInstitutionsError] = useState<APIError>();
    const [institutionSearchText, setInstitutionSearchText] = useState<Option>(null);
    const [userNearestCity, setUserNearestCity] = useState('');

    ////////////////////////////////
    // async data retrieval actions
    ///////////////////////////////

    const getUserCoordinates = () => {
        const geolocationAPI = !process.env.MOCK_GEO ? new MockGeoLocation() : navigator.geolocation
        if (!geolocationAPI) {
            console.log("Geolocation API is not available in your browser!");
        } else {
            return new Promise<ICoordinates>((res, rej) => {
                geolocationAPI.getCurrentPosition(
                    (position) => {
                        const { coords } = position;
                        setLat(coords.latitude);
                        setLong(coords.longitude);
                        res(coords);
                        getLocationData({ latitude: coords.latitude, longitude: coords.longitude }, 100.0).then(e => setUserNearestCity(e.city));
                    },
                    (error) => {
                        rej(error)
                        console.log("Something went wrong getting your position!");
                    },
                );

            })
        }
    };

    async function retrieveDogs() {
        try {
            const res = await DataProvider.get(EndPoint.DOGS);
            let dogs = res.data;
            setAllDogsList(dogs);
            setDogsWithinProximityList(dogs);
        } catch (err) {
            setDogListError(err);
        }
    }


    async function retrieveFilters(): Promise<FilterSet> {
        try {
            const res = await DataProvider.get(EndPoint.FILTERS);
            let filters = res.data;
            setFilterOptions(filters);
            return filters;
        } catch (err) {
            setFilterError(err);
        }
    }

    async function retrieveInstitutions() {
        try {
            const res = await DataProvider.get(EndPoint.PARTNERSHIPS);
            let institutions = res.data as Institution[];
            setInstitionList(institutions);
        } catch (err) {
            setInstitutionsError(err);
        }
    }

    const retrieveCustomView = async (coordinates: ICoordinates, filters?: Option[]) => {
        let userView = localStorage.getItem('p4p-view');
        if (userView) {
            let custom = JSON.parse(userView) as CustomView;
            retrieveProximityInstitutions(custom, coordinates, filters);
        }
    }

    type LocationData = { city: string, institutionIds: string[] };

    async function getLocationData(coordinates: ICoordinates, distance: number): Promise<LocationData> {
        let endpointUrl = EndPoint.PERSON_PROXIMITY;
        let formData = { distance, ...coordinates };
        const res = await DataProvider.get(endpointUrl, formData);
        return res.data as LocationData;
    }

    const retrieveProximityInstitutions = async (customView: CustomView, coordinates?: ICoordinates, institutionOptions = filterOptions?.[FILTER_KEY[FILTER_TYPE.INSTITUTION]] || []) => {
        let distance = customView.range;

        let useMyLocation = customView.selection === 'location';
        let endpointUrl = '';
        let formData = {
            "distance": distance,
            "location": null
        };
        if (customView.selection === 'location') {
            if (!coordinates) {
                return console.error('Failed to load location data because no coordinates were provided')
            }
            formData = { ...formData, ...coordinates };
            endpointUrl = EndPoint.PERSON_PROXIMITY;
        } else {
            let location = customView.location.cityZip;
            // if locationText is a number set the endpoint url to zipcode_proximity else set to city_proximity
            endpointUrl = Number.isInteger(Number(location)) ? EndPoint.ZIPCODE_PROXIMITY : EndPoint.CITY_PROXIMITY;
            formData = { ...formData, "location": location };
        }
        try {
            const res = await DataProvider.get(endpointUrl, formData);
            let data = res.data as LocationData | string[];
            if (res.status < 500) {
                function isLocationData(d: unknown): d is LocationData { return useMyLocation } // we know that locationdata is returned if the endpoint was usemylocation
                const institutionIds = isLocationData(data) ? data.institutionIds : data;
                let location: ILocation = {
                    cityZip: isLocationData(data) ? data.city : customView.location.cityZip
                }
                customView.location = location;
                setCustomView(customView);
                const optionsToSelect = institutionOptions.filter(o => institutionIds.includes(o.value))
                selectFilters(optionsToSelect, FILTER_KEY[FILTER_TYPE.INSTITUTION], true);
                return optionsToSelect;
            }
        } catch (err) {
            console.log(err)

        }
    }

    useEffect(() => { // initiate async data retrieval actions on load
        async function initializeData() {
            setLoading(true);
            retrieveDogs();
            const filters = await retrieveFilters();
            if (filters) {
                const coords = await getUserCoordinates();
                await retrieveCustomView(coords, filters[FILTER_KEY[FILTER_TYPE.INSTITUTION]]);
            }
            setTimeout(() => {
                setLoading(false); // allow timeout to eliminate dog count number in header from flashing
            }, 500);
        }
        initializeData()
        retrieveInstitutions()
    }, []);

    /////////////////////////////////

    /////////////////////////////////
    // Actions for the search panel
    /////////////////////////////////

    function commitViewForm(customView: CustomView) {
        if (customView) {
            localStorage.setItem('p4p-view', JSON.stringify(customView));
            return retrieveProximityInstitutions(customView, { latitude: lat, longitude: long });
        } else resetCustomView()
    }

    function resetCustomView() {
        localStorage.removeItem('p4p-view');
        setCustomView(null);
        selectFilters([], FILTER_KEY[FILTER_TYPE.INSTITUTION]);
    }



    /////////////////////////////////////////////
    // Precalculated lists based on filter rules
    ////////////////////////////////////////////

    const filteredDogsList = useMemo(() => {
        // override the institution behaviors if a custom view is selected and no institutions match the custom filter
        // the additional behavior here makes it impossible to match any dogs, so there are necessarily no dogs in the list
        if (customView && !selectedFilters[FILTER_KEY[FILTER_TYPE.INSTITUTION]]?.length) return [];
        return filterList(allDogsList, selectedFilters).sort((a, b) => sortDogs(a, b, dogListSortType));
    }, [selectedFilters, allDogsList, dogListSortType, customView]);

    const filteredInstitutionList = useMemo(() => {
        const safeInstitutions = institutionList || [];
        if (!institutionSearchText) return institutionList;
        let matchingInstitutions = safeInstitutions.filter(institution => institution.name === institutionSearchText.label);
        if (matchingInstitutions.length > 0) return matchingInstitutions;
        safeInstitutions.forEach(inst => inst.shelters.forEach(shelter => {
            if (shelter.name === institutionSearchText.label && !matchingInstitutions.includes(inst)) {
                matchingInstitutions.push(inst);
            }
        }));
        return matchingInstitutions;
    }, [institutionList, institutionSearchText]);



    const validFilters = useMemo(() => {
        const set: FilterSet = {};
        if (filterOptions) { // make sure that filters retrieved from api is populated before filtering specific types and counting totals
            for (let fType in filterOptions) {
                set[fType] = filterOptions[fType]
                    .map(o => ({
                        ...o,
                        filteredCount: filterList(filteredDogsList, { [fType]: [o] }).length // make filter count (all filters besides institution) filter from count of dogs within proximity only
                    })).sort((a, b) => b.filteredCount - a.filteredCount)
            }
        }
        return set;
    }, [allDogsList, filterOptions, filteredDogsList])


    /////////////////////////////////////


    //////////////////////////
    // Institution Actions
    /////////////////////////

    function updateInstitutionSearchText(searchValue: Option) {
        setInstitutionSearchText(searchValue);
    }

    //////////////////////////////////////////=

    //////////////////////////
    //  Filter Actions
    //////////////////////////

    /*
    / function for when a select-box filter is selected.
    */
    function selectFilters(filters: Option[], key: string, isAutoFilter?: boolean) {
        if (key === FILTER_KEY[FILTER_TYPE.INSTITUTION] && !isAutoFilter) {
            setCustomView(null);
            localStorage.removeItem('p4p-view');
        }
        if (!!filters.length) {
            setSelectedFilters(s => ({ ...s, [key]: filters }))
        } else {
            let filters = { ...selectedFilters };
            delete filters[key];
            setSelectedFilters(filters);
        }
    }

    function isSameOption(a: Option, b: Option) {
        return a.type === b.type && a.value === b.value
    }

    const removeFilter = (filterToRemove: Option, type: FILTER_TYPE) => {
        if (type === FILTER_TYPE.CUSTOM_VIEW) {
            resetCustomView();
            return;
        }
        let filterType = FILTER_KEY[type];
        let remainingFilters = selectedFilters[filterType].filter(existingFilter => !isSameOption(existingFilter, filterToRemove));
        selectFilters(remainingFilters || [], filterType);
    }

    const removeAllFilters = () => setSelectedFilters(sf => ({ [FILTER_KEY[FILTER_TYPE.INSTITUTION]]: customView ? sf[FILTER_KEY[FILTER_TYPE.INSTITUTION]] : [] }));


    return (
        <DataContext.Provider value={{
            loading,
            dogListError,
            filteredDogsList,
            sortType: dogListSortType,
            setSortType: setDogListSortType,
            selectedDogIndex,
            setSelectedDogIndex,
            customView,
            commitViewForm,
            userNearestCity,
            filterOptions,
            selectedFilters,
            filterError,
            validFilters,
            selectFilters,
            // saveFilters,
            removeFilter,
            removeAllFilters,
            filteredInstitutionList,
            institutionsError,
            updateInstitutionSearchText,
            institutionSearchText,
            // showSaveSearchBtn,
            // userSave,
            // loadSavedFilters,
            // clearSavedFilters,
            dogsWithinProximityList
        }}
        >
            {props.children}
        </DataContext.Provider>
    );

};